import {take} from 'rxjs';
import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {AnimalRepository} from "../states/animal.repository";
import {AnimalService} from "../services/animal.service";
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {AnimalTypeModel} from "../models/animal-type.model";
import {AnimalCoatTypeModel} from "../models/animal-coat-type.model";
import {AnimalGenderModel} from "../models/animal-gender.model";
import {AnimalEditService} from "../services/animal-edit.service";
import {AnimalModel} from "../models/animal.model";
import {AnimalCategoryModel} from "../models/animal-category.model";
import {AnimalFilter} from "../animal-filters/animal-filter.model";
import {AnimalCharacteristicsModel} from "../models/animal-characteristics.model";
import {DatePipe} from '@angular/common';
import {HelperService} from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-animal-edit-dialog',
  templateUrl: './animal-edit-dialog.component.html',
  styleUrls: ['./animal-edit-dialog.component.scss'],
  standalone: false
})
export class AnimalEditDialogComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | undefined;
  visible: boolean = false;
  formGroup: FormGroup;
  alive: boolean = true;
  categoryFilters: AnimalFilter[] = [];
  animalTypeOptions: AnimalTypeModel[] = [];
  animalCategoryOptions: AnimalCategoryModel[] = [];
  coatTypeOptions: AnimalCoatTypeModel[] = [];
  genderOptions: AnimalGenderModel[] = [];
  characteristicsOptions: AnimalCharacteristicsModel[] | undefined;
  mother: AnimalModel | undefined;
  isYearOnlyFlag = false;
  weightInput: string = '';
  weightInputInvalid: boolean = false;
  protectedOldBookControl: FormControl<string | null> | null = null;


  constructor(
    private readonly _animalEditService: AnimalEditService,
    private readonly _animalService: AnimalService,
    private readonly _animalRepository: AnimalRepository,
    private readonly _helperService: HelperService,
    private readonly datePipe: DatePipe
  ) {
    this.formGroup = new FormGroup({
      type: new FormControl<string | null>(null, Validators.required),
      category: new FormControl<AnimalCategoryModel | null>(null, Validators.required),
      sex: new FormControl<AnimalGenderModel | null>(null, Validators.required),
      oldBookNumber: new FormControl<string | null>(null),
      oldBookNumberList: new FormArray<FormControl>([]),
      name: new FormControl<string | null>(null),
      breed: new FormControl<string | null>(null, Validators.required),
      nameOld: new FormControl<string | null>(null),
      nameNew: new FormControl<string | null>(null),
      birthApprox: new FormControl<boolean | null>(null),
      birthdayType: new FormControl<boolean | null>(false),
      birthYear: new FormControl<Date | null>(null),
      birthday: new FormControl<Date | null>(null),
      birthPlace: new FormControl<string | null>(null),
      coatType: new FormControl<AnimalCoatTypeModel[] | null>(null),
      coatColor: new FormControl<string | null>(null),
      badge: new FormControl<string | null>(null),
      specialNotice: new FormControl<string | null>(null),
      size: new FormControl<number | null>(null),
      weight: new FormControl<number | null>(null),
      transponderNumber: new FormArray<FormControl>([]),
      lifeNumber: new FormControl<string | null>(null),
      ringNumber: new FormControl<string | null>(null),
      petPassportNumber: new FormControl<string | null>(null),
      equinePassportNumber: new FormControl<string | null>(null),
      earTag: new FormControl<string | null>(null),
      tattoo: new FormControl<string | null>(null),
      tattooLocation: new FormControl<string | null>(null),
      specialInformation: new FormControl<string | null>(null),
      castrated: new FormControl<boolean>(false),
      castratedDate: new FormControl<Date | null>(null),
      castratedNote: new FormControl<string | null>(null),
      chemicalCastratedDate: new FormControl<Date | null>(null),
      sterilized: new FormControl<boolean | null>(null),
      sterilizedDate: new FormControl<Date | null>(null),
      neutered: new FormControl<boolean | null>(null),
      internalDetails: new FormControl<AnimalCharacteristicsModel[] | null>(null),
      externalDetails: new FormControl<AnimalCharacteristicsModel[] | null>(null),
      externalShortDescription: new FormControl<string | null>(null),
      externalDescription: new FormControl<string | null>(null),
      websiteMetaDescription: new FormControl<string | null>(null),
      websiteMetaTitle: new FormControl<string | null>(null),
      motherId: new FormControl<number | null>(null)
    });

    this.formGroup.get('castratedDate')?.valueChanges.subscribe(() => {
      this.onCastratedDatesChange();
    });

    this.formGroup.get('chemicalCastratedDate')?.valueChanges.subscribe(() => {
      this.onCastratedDatesChange();
    });
  }

  ngOnInit(): void {
    this.visible = true;
    this.categoryFilters = this._animalService.getFilterCategories();
    this.animalCategoryOptions = this._animalService.getAnimalCategories();
    this.animalTypeOptions = this._animalService.getAnimalTypes();
    this.genderOptions = this._animalService.getGenderOptions();
    this.coatTypeOptions = this._animalService.getCoatTypes();

    if (this.animal) {
      if (this.animal.category) {
        this.setCharacteristicOptions(this.animal.category);
      }

      this.formGroup.patchValue({
        name: this.animal.name,
        nameOld: this.animal.nameOld,
        nameNew: this.animal.nameNew,
        type: this.animal.type,
        breed: this.animal.breed,
        birthApprox: this.animal.birthApprox,
        birthPlace: this.animal.birthPlace,
        category: this.animal.category?.toLowerCase(),
        sex: this.animal.sex,
        coatType: this.animal.coatType,
        coatColor: this.animal.coatColor,
        specialNotice: this.animal.specialNotice,
        badge: this.animal.badge,
        size: (this.animal.size !== null) ? this.animal.size / 10 : null,
        weight: (this.animal.weight !== null && this.animal.weight !== undefined) ? this.animal.weight / 1000 : null,
        transponderNumber: this.animal.transponderNumber,
        lifeNumber: this.animal.lifeNumber,
        ringNumber: this.animal.ringNumber,
        petPassportNumber: this.animal.petPassportNumber,
        equinePassportNumber: this.animal.equinePassportNumber,
        earTag: this.animal.earTag,
        tattoo: this.animal.tattoo,
        tattooLocation: this.animal.tattooLocation,
        specialInformation: this.animal.specialInformation,
        castrated: this.animal.castrated,
        castratedNote: this.animal.castratedNote,
        chemicalCastratedDate: this.animal.chemicalCastratedDate,
        sterilized: this.animal.sterilized,
        internalDetails: this.animal.internalDetails,
        externalDetails: this.animal.externalDetails,
        externalShortDescription: this.animal.externalShortDescription,
        externalDescription: this.animal.externalDescription,
        websiteMetaDescription: this.animal.websiteMetaDescription,
        websiteMetaTitle: this.animal.websiteMetaTitle
      });

      // Gewicht initialisieren
      const kg = (this.animal.weight !== null && this.animal.weight !== undefined) ? this.animal.weight / 1000 : null;
      this.weightInput = this.formatWeightInput(kg);

      if (this.animal.motherId) {
        this._animalRepository.getById(this.animal.motherId).pipe(take(1)).subscribe((mother) => {
          if (!mother) {
            return;
          }
          this.mother = mother;
          this.formGroup.get('motherId')?.setValue(mother.id);
        });
      }

      if (this.animal.castratedDate != null) {
        this.formGroup.patchValue({
          castratedDate: this.datePipe.transform(this.animal.castratedDate, 'yyyy-MM-dd'),
          castrated: true,
        });
      }

      if (this.animal.castrated) {
        this.formGroup.patchValue({
          castrated: true,
        });
      }

      if (this.animal.chemicalCastratedDate != null) {
        this.formGroup.patchValue({
          chemicalCastratedDate: this.datePipe.transform(this.animal.chemicalCastratedDate, 'yyyy-MM-dd'),
          castrated: true,
        });
      }

      if (this.animal.sterilizedDate != null) {
        this.formGroup.patchValue({
          sterilizedDate: this.datePipe.transform(this.animal.sterilizedDate, 'yyyy-MM-dd'),
          sterilized: true,
        });
      }

      if (this.animal.birthday != null) {
        this.formGroup.patchValue({
          birthdayType: false,
          birthday: new Date(this.animal.birthday),
          birthYear: null,
        });
      }
      if (this.animal.birthYear != null) {
        const date = new Date(this.animal.birthYear);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const lastDay = new Date(year, 11, 31);
          this.formGroup.patchValue({
            birthdayType: true,
            birthday: null,
            birthYear: lastDay,
          });
        }
      }
      const oldBookArray = this.oldBookNumberList;

      while (oldBookArray.length > 0) {
        oldBookArray.removeAt(0);
      }

      if (!this.animal.oldBookNumberList?.length && this.animal.oldBookNumber) {
        oldBookArray.push(new FormControl<string | null>({
          value: this.animal.oldBookNumber,
          disabled: true
        }));
      } else if (this.animal.oldBookNumberList && this.animal.oldBookNumberList.length > 0) {
        this.animal.oldBookNumberList.forEach(num =>
          oldBookArray.push(new FormControl<string | null>({
            value: num,
            disabled: true
          }))
        );
      }

      oldBookArray.push(new FormControl<string | null>(null));

      this.protectedOldBookControl =
        oldBookArray.length > 0 ? (oldBookArray.at(0) as FormControl<string | null>) : null;

      this.updateOldBookNumberDisabledStates();
    }

    if (this.animal && this.animal.transponderNumber) {
      const numbers = this.animal.transponderNumber.length > 0 ? this.animal.transponderNumber : [''];
      numbers.forEach(num =>
        (this.formGroup.get('transponderNumber') as FormArray).push(new FormControl(num))
      );
    } else {
      (this.formGroup.get('transponderNumber') as FormArray).push(new FormControl(''));
    }
  }

  get transponderNumbers(): FormArray {
    return this.formGroup.get('transponderNumber') as FormArray;
  }

  addTransponderNumber() {
    this.transponderNumbers.push(new FormControl(''));
  }

  removeTransponderNumber(index: number) {
    if (this.transponderNumbers.length > 1) {
      this.transponderNumbers.removeAt(index);
    }
  }

 get oldBookNumberList(): FormArray<FormControl<string | null>> {
  return this.formGroup.get('oldBookNumberList') as FormArray<FormControl<string | null>>;
}

get oldBookNumberControlsReversed(): FormControl<string | null>[] {
  return [...this.oldBookNumberList.controls].reverse() as FormControl<string | null>[];
}
addOldBookNumber() {
  this.oldBookNumberList.push(new FormControl<string | null>(null));
  this.updateOldBookNumberDisabledStates();
}

removeOldBookNumberControl(ctrl: FormControl<string | null>) {
  const index = this.oldBookNumberList.controls.indexOf(ctrl);
  if (index > -1) {
    this.oldBookNumberList.removeAt(index);
    this.updateOldBookNumberDisabledStates();
  }
}


private updateOldBookNumberDisabledStates(): void {
  const arr = this.oldBookNumberList;
  const lastIndex = arr.length - 1;

  arr.controls.forEach((ctrl, index) => {
    if (index === lastIndex) {
      ctrl.enable({ emitEvent: false });
    } else {
      ctrl.disable({ emitEvent: false });
    }
  });
}

  handleBarcodeTransponderScanned(event: { field?: string; value: string }) {
    const field = event.field;
    const code = event.value;

    if (!field) {
      console.warn('Kein Feld übergeben für Barcode:', code);
      return;
    }
    const input = document.getElementById(field) as HTMLInputElement;
    if (input) {
      input.value = code;
    }
    const idx = Number(field.split('_')[1]);
    this.transponderNumbers.at(idx).setValue(code);
  }


  setCharacteristicOptions(category: string) {
    const animalFilter = this._animalService.getAnimalFilterByCategory(category);
    if (animalFilter) {
      this.characteristicsOptions = this._animalService.getCharacteristicsForCategory(animalFilter);
    }
  }

  closeModal() {
    this._animalEditService.toggleEditDialog({});
  }

  addMotherAnimal(animal: AnimalModel) {
    this.formGroup.get('motherId')?.setValue(animal.id);
    this.mother = animal;
  }

  handleBarcodeScanned(event: { field?: string; value: string }) {
    if (!event.field) {
      console.warn('Kein Feld angegeben für Barcode:', event.value);
      return;
    }

    this.formGroup.get(event.field)?.setValue(event.value);
  }

  get currentBirthday(): string | null {
    const isYearOnly = this.formGroup.get('birthdayType')?.value;
    const dateValue = isYearOnly
      ? this.formGroup.get('birthYear')?.value
      : this.formGroup.get('birthday')?.value;

    if (!dateValue) return null;

    return this.datePipe.transform(dateValue, 'yyyy-MM-dd');
  }

  onYearOnlyChanged(value: boolean) {
    this.isYearOnlyFlag = value;
    this.formGroup.get('birthdayType')?.setValue(value);
  }

  onDelayedBirthdayChange(date: string) {
    setTimeout(() => this.onBirthdayChange(date), 0);
  }

  onBirthdayChange(date: string) {
    if (this.isYearOnlyFlag) {
      const match = /^\d{4}-01-01$/.exec(date);
      if (match) {
        const year = +date.split('-')[0];
        const lastDayOfYear = new Date(year, 11, 31);
        this.formGroup.get('birthYear')?.setValue(lastDayOfYear);
      } else {
        this.formGroup.get('birthYear')?.setValue(null);
      }
      this.formGroup.get('birthday')?.reset();
    } else {
      const validDate = new Date(date);
      if (!isNaN(validDate.getTime())) {
        this.formGroup.get('birthday')?.setValue(validDate);
      } else {
        this.formGroup.get('birthday')?.setValue(null);
      }
      this.formGroup.get('birthYear')?.reset();
    }
  }


  saveChanges() {
    if (this.animal === undefined) {
      this.closeModal();
      return;
    }

    if (this.formGroup.invalid || this.weightInputInvalid) {
      return;
    }

    if (this.formGroup.get('birthdayType')?.value === true) {
      this.formGroup.patchValue({birthday: null});
    } else {
      this.formGroup.patchValue({birthYear: null});
    }

    if (this.formGroup.get('size')?.value !== null && this.formGroup.get('size')?.value !== undefined && this.formGroup.get('size')?.value !== 0) {
      this.formGroup.patchValue({size: this.formGroup.get('size')?.value * 10});
    } else {
      this.formGroup.patchValue({size: null});
    }

    // Gewicht ins Backend-Format (Gramm, Integer) umwandeln
    const weightVal = this.formGroup.get('weight')?.value;
    if (weightVal !== null && weightVal !== undefined && weightVal !== '') {
      this.formGroup.patchValue({
        weight: Math.round(Number(weightVal) * 1000)
      });
    } else {
      this.formGroup.patchValue({weight: null});
    }

    if (this.formGroup.get('castratedDate')?.value !== null) {
      this.formGroup.patchValue({castrated: true});
    }

    if (this.formGroup.get('chemicalCastratedDate')?.value !== null) {
      this.formGroup.patchValue({castrated: true});
    }

    if (this.formGroup.get('castratedDate')?.value === '' && this.formGroup.get('castratedDate')?.value === null
      && this.formGroup.get('chemicalCastratedDate')?.value === '' && this.formGroup.get('chemicalCastratedDate')?.value === null) {
      this.formGroup.patchValue({castrated: false});
    }

    if (this.formGroup.get('sterilizedDate')?.value === '' || this.formGroup.get('sterilizedDate')?.value !== null) {
      this.formGroup.patchValue({sterilized: true});
    } else {
      this.formGroup.patchValue({sterilized: false});
    }
    if (this.formGroup.get('motherId')?.value) {
      this.formGroup.addControl('mother', new FormControl<{} | null>(null));
      this.formGroup.patchValue({mother: {id: this.formGroup.get('motherId')?.value}});
      this.formGroup.removeControl('motherId');
    }

    const transponderNumbers = (this.formGroup.get('transponderNumber') as FormArray).getRawValue();
    this.formGroup.patchValue({transponderNumber: transponderNumbers});
    const oldBookNumberArray = this.formGroup.get('oldBookNumberList') as FormArray;
    if (oldBookNumberArray.length > 0) {
      const sanitizedList = oldBookNumberArray.getRawValue()
        .map((v: string | null) => (v ?? '').toString().trim())
        .filter(v => v !== '')
        .map(v => this._helperService.sanitizeOldBookNumber(v));

      while (oldBookNumberArray.length > 0) {
        oldBookNumberArray.removeAt(0);
      }

      sanitizedList.forEach(v => oldBookNumberArray.push(new FormControl(v)));
    }

    this._animalRepository.update(this.animal.id, this.formGroup.value).pipe(take(1)).subscribe(() => {
      if (this.animal && this.animal.id) {
        this._animalRepository.getById(this.animal.id).pipe(take(1)).subscribe((animal) => {
          this.animal = animal;
        });
      }
    });

    this.closeModal();
  }

  onCastratedDatesChange() {
    const castratedDate = this.formGroup.get('castratedDate')?.value;
    const chemicalCastratedDate = this.formGroup.get('chemicalCastratedDate')?.value;

    if (castratedDate || chemicalCastratedDate) {
      this.formGroup.get('castrated')?.setValue(true);
    } else {
      this.formGroup.get('castrated')?.setValue(false);
    }
  }

  ngOnDestroy() {
    this.alive = false;
  }

  formatWeightInput(val: number | null): string {
    if (val === null || val === undefined) return '';
    return val.toLocaleString('de-DE', {minimumFractionDigits: 3, maximumFractionDigits: 3});
  }

  onWeightInput(event: any) {
    const value = event.target.value;
    this.weightInput = value;

    const valid = /^(\d{0,6}([,.]\d{0,3})?)?$/.test(value);
    if (!valid) {
      this.weightInputInvalid = true;
      this.formGroup.get('weight')?.setValue(null);
      return;
    }

    const normalized = value.replace(',', '.');
    const num = parseFloat(normalized);

    const nachkommastellen = (normalized.split('.')[1] || '').length;
    if (isNaN(num) || nachkommastellen > 3) {
      this.weightInputInvalid = true;
      this.formGroup.get('weight')?.setValue(null);
    } else {
      this.weightInputInvalid = false;
      this.formGroup.get('weight')?.setValue(num);
    }
  }
}
